import React, { useRef, useState } from 'react';
import { Hand } from 'lucide-react';
import './App.css';

const useLongPress = (
  onClick: () => void,
  onLongPress: () => void,
  delay = 700,
  linger = 0
) => {
  const timerRef = useRef<number | null>(null);
  const graceRef = useRef<number | null>(null);
  const lingerRef = useRef<number | null>(null);
  const [holding, setHolding] = useState(false);
  const [flash, setFlash] = useState(false);

  const start = () => {
    graceRef.current = window.setTimeout(() => {
      setHolding(true);
      graceRef.current = null;
    }, 100);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      onLongPress();
      setFlash(true);
      window.setTimeout(() => setFlash(false), 220);
      if (linger > 0) {
        lingerRef.current = window.setTimeout(() => {
          setHolding(false);
          lingerRef.current = null;
        }, linger);
      } else {
        setHolding(false);
      }
    }, delay);
  };

  const stop = (triggerClick: boolean) => {
    if (graceRef.current) {
      window.clearTimeout(graceRef.current);
      graceRef.current = null;
    }
    if (lingerRef.current) {
      window.clearTimeout(lingerRef.current);
      lingerRef.current = null;
    }
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
      if (triggerClick) {
        onClick();
      }
    }
    setHolding(false);
  };

  return {
    holding,
    flash,
    onPointerDown: start,
    onPointerUp: () => stop(true),
    onPointerLeave: () => stop(false),
    onPointerCancel: () => stop(false),
  };
};

const App = () => {
  const [splitMenu, setSplitMenu] = useState<'load' | 'unload' | null>(null);

  const [modeManual, setModeManual] = useState(false);
  const noop = () => {};

  const splitLoadClick = () => setSplitMenu(null);
  const splitManualLoadClick = () => setSplitMenu(null);
  const splitUnloadClick = () => setSplitMenu(null);
  const splitManualUnloadClick = () => setSplitMenu(null);

  const longPressLoad = useLongPress(noop, noop, 1000, 200);
  const longPressUnload = useLongPress(noop, noop, 1000, 200);
  const inlineManualLoadClick = () => {};
  const inlineManualUnloadClick = () => {};
  const inlineManualLoad = useLongPress(
    inlineManualLoadClick,
    inlineManualLoadClick,
    750,
    200
  );
  const inlineManualUnload = useLongPress(
    inlineManualUnloadClick,
    inlineManualUnloadClick,
    750,
    200
  );

  return (
    <div className="page">
      <header className="hero">
        <div>
          <p className="eyebrow">ATCI Buttons - Load + Manual - Unload + Manual</p>
          <h1>ATCI Buttons</h1>
          <p className="subhead">
            Four options for pairing load/unload with manual actions.
          </p>
        </div>
        <div className="legend">
          <div className="swatch" aria-hidden="true" />
          <div>
            <p className="legend-title">Primary action</p>
            <p className="legend-note">#3F85C7 for tap-ready state</p>
          </div>
        </div>
      </header>

      <section className="grid">
        <article className="card card-large">
          <div className="card-header">
            <h2>1. Split Button (Dropdown)</h2>
            <p>
              Tap main button for primary action. Use the arrow to pick manual
              variants.
            </p>
          </div>
          <div className="control-list">
            <div className="action-pair">
              <div className="action-block">
                <div className="split-wrap">
                  <div className="split-button">
                    <button className="btn primary" onClick={splitLoadClick}>
                      Load Tool
                    </button>
                    <button
                      className="btn split-toggle"
                      aria-haspopup="true"
                      aria-expanded={splitMenu === 'load'}
                      onClick={() =>
                        setSplitMenu(splitMenu === 'load' ? null : 'load')
                      }
                    >
                      ▼
                    </button>
                  </div>
                  {splitMenu === 'load' && (
                    <div className="split-menu">
                      <button onClick={splitLoadClick}>Load</button>
                      <button onClick={splitManualLoadClick}>Manual Load</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="action-block">
                <div className="split-wrap">
                  <div className="split-button">
                    <button className="btn primary" onClick={splitUnloadClick}>
                      Unload Tool
                    </button>
                    <button
                      className="btn split-toggle"
                      aria-haspopup="true"
                      aria-expanded={splitMenu === 'unload'}
                      onClick={() =>
                        setSplitMenu(splitMenu === 'unload' ? null : 'unload')
                      }
                    >
                      ▼
                    </button>
                  </div>
                  {splitMenu === 'unload' && (
                    <div className="split-menu">
                      <button onClick={splitUnloadClick}>Unload</button>
                      <button onClick={splitManualUnloadClick}>
                        Manual Unload
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </article>

        <article className="card card-large">
          <div className="card-header">
            <h2>2. Mode Toggle + Single Button</h2>
            <p>
              Toggle Manual Mode once to switch both buttons between normal and
              manual actions.
            </p>
          </div>
          <div className="control-list">
            <div className="mode-toggle-row">
              <span className="control-label">Manual Mode</span>
              <label className="switch" aria-label="Manual Mode">
                <input
                  type="checkbox"
                  checked={modeManual}
                  onChange={(event) => setModeManual(event.target.checked)}
                />
                <span className="switch-track" aria-hidden="true" />
              </label>
            </div>
            <div className="action-pair">
              <div className="action-block">
                <div className="toggle-stack">
                  <button className="btn primary">
                    {modeManual ? 'Manual Load' : 'Load Tool'}
                  </button>
                </div>
              </div>
              <div className="action-block">
                <div className="toggle-stack">
                  <button className="btn primary">
                    {modeManual ? 'Manual Unload' : 'Unload Tool'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        <article className="card card-large">
          <div className="card-header">
            <h2>3. Long Press for Manual</h2>
            <p>
              Tap for the standard action. Hold for 1s to trigger manual with a
              left-to-right fill.
            </p>
          </div>
          <div className="control-list">
            <div className="action-pair">
              <div className="action-block">
                <button
                  className={`btn primary hold-button ${
                    longPressLoad.holding ? 'holding' : ''
                  }`}
                  data-flash={longPressLoad.flash}
                  onPointerDown={longPressLoad.onPointerDown}
                  onPointerUp={longPressLoad.onPointerUp}
                  onPointerLeave={longPressLoad.onPointerLeave}
                  onPointerCancel={longPressLoad.onPointerCancel}
                >
                  <span className="hold-fill" aria-hidden="true" />
                  <span className="hold-label">Load Tool</span>
                </button>
                <span className="hold-note">Hold for manual</span>
              </div>
              <div className="action-block">
                <button
                  className={`btn primary hold-button ${
                    longPressUnload.holding ? 'holding' : ''
                  }`}
                  data-flash={longPressUnload.flash}
                  onPointerDown={longPressUnload.onPointerDown}
                  onPointerUp={longPressUnload.onPointerUp}
                  onPointerLeave={longPressUnload.onPointerLeave}
                  onPointerCancel={longPressUnload.onPointerCancel}
                >
                  <span className="hold-fill" aria-hidden="true" />
                  <span className="hold-label">Unload Tool</span>
                </button>
                <span className="hold-note">Hold for manual</span>
              </div>
            </div>
          </div>
        </article>

        <article className="card card-large">
          <div className="card-header">
            <h2>4. Inline Secondary Action Icon</h2>
            <p>
              Primary action stays large. Hold the small button for 0.75s to
              trigger the manual action with a fill.
            </p>
          </div>
          <div className="control-list">
            <div className="action-pair">
              <div className="action-block">
                <div className="inline-action">
                  <button className="btn primary">
                    Load Tool
                  </button>
                  <button
                    className={`icon-button hold-button ${
                      inlineManualLoad.holding ? 'holding' : ''
                    }`}
                    aria-label="Manual Load"
                    data-flash={inlineManualLoad.flash}
                    onPointerDown={inlineManualLoad.onPointerDown}
                    onPointerUp={inlineManualLoad.onPointerUp}
                    onPointerLeave={inlineManualLoad.onPointerLeave}
                    onPointerCancel={inlineManualLoad.onPointerCancel}
                  >
                    <span className="hold-fill" aria-hidden="true" />
                    <span className="hold-label">
                      <Hand className="hand-icon" aria-hidden="true" />
                    </span>
                  </button>
                </div>
              </div>
              <div className="action-block">
                <div className="inline-action">
                  <button className="btn primary">
                    Unload Tool
                  </button>
                  <button
                    className={`icon-button hold-button ${
                      inlineManualUnload.holding ? 'holding' : ''
                    }`}
                    aria-label="Manual Unload"
                    data-flash={inlineManualUnload.flash}
                    onPointerDown={inlineManualUnload.onPointerDown}
                    onPointerUp={inlineManualUnload.onPointerUp}
                    onPointerLeave={inlineManualUnload.onPointerLeave}
                    onPointerCancel={inlineManualUnload.onPointerCancel}
                  >
                    <span className="hold-fill" aria-hidden="true" />
                    <span className="hold-label">
                      <Hand className="hand-icon" aria-hidden="true" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default App;
